import classNames from 'classnames';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import Select, { GroupBase, Props, components, StylesConfig, mergeStyles } from 'react-select';
import Creatable, { CreatableProps } from 'react-select/creatable';
import AsyncCreatable, { AsyncCreatableProps } from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { Typography } from '../typography/typography';
import Icon from '../icons/icon';
import { FieldDescription, FieldDescriptionProps, FieldLabel, FieldLabelProps } from '../field/field';

type SelectFormProps = FieldLabelProps &
  FieldDescriptionProps & {
    type?: 'input' | 'inline';
    dataCy?: string;
  };

type CreatableSelectFormProps = SelectFormProps & {
  creatableOptionText?: string;
  onClickCreatableOption?: () => any;
};

// Default vars
const defaultStyle = <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  type: 'input' | 'inline',
): StylesConfig<Option, IsMulti, Group> => {
  return {
    control: (baseStyles) => ({
      ...baseStyles,
      cursor: 'pointer',
      ...(type === 'input'
        ? { borderRadius: '9px', borderColor: '#D0D5DD !important', boxShadow: 'none' }
        : { borderColor: 'transparent !important', boxShadow: 'none' }),
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      'input[aria-readonly]': { padding: '0px !important' },
      ...(type === 'inline' && { padding: '0px', margin: '0px' }),
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      minHeight: '0px',
      height: 'auto',
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      paddingRight: '16px',
      ...(type === 'inline' && { padding: '0px', margin: '0px' }),
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      width: 'fit-content',
      minWidth: '100%',
      borderRadius: '9px',
      zIndex: 12,
      border: '1px solid #F2F4F7',
      boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    }),
    menuList: (baseStyles) => ({
      ...baseStyles,
      padding: '4px',
    }),
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      zIndex: 9999,
    }),
    option: (baseStyles, { isFocused, isDisabled }) => ({
      ...baseStyles,
      cursor: 'pointer',
      borderRadius: '6px',
      color: '#1d2939',
      backgroundColor: isDisabled ? 'var(--grey-lighter)' : isFocused ? '#f2f4f7' : 'transparent',
      opacity: isDisabled ? 0.5 : 1,
    }),
  };
};

const defaultComponents = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  type: 'input' | 'inline',
  dataCy?: string,
  creatableOptionText?: string,
): Partial<SelectComponents<Option, IsMulti, Group>> => {
  return {
    Placeholder: (props) => (
      <components.Placeholder {...props}>
        <Typography type="text-sm">{props.children}</Typography>
      </components.Placeholder>
    ),
    Option: (props) => {
      //@ts-expect-error __isNew__
      const isCreateOption = props.data.__isNew__ === true;

      return (
        <components.Option {...props}>
          <div
            className={classNames('flex items-center gap-[6px] quipli-v2', {
              'justify-between': !isCreateOption,
            })}
          >
            {isCreateOption ? (
              <>
                <Icon icon="Plus" stroke="var(--quipli-black-800)" />
                <Typography type="text-sm">{creatableOptionText || 'Create'}</Typography>
              </>
            ) : (
              <>
                <Typography type="text-sm">{props.children}</Typography>
                {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
              </>
            )}
          </div>
        </components.Option>
      );
    },
    DropdownIndicator: (props) => (
      <components.DropdownIndicator {...props}>
        {type === 'input' ? (
          <Icon icon={props.selectProps.menuIsOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
        ) : (
          <i
            className={classNames('fa-solid', {
              'fa-caret-down': !props.selectProps.menuIsOpen,
              'fa-caret-up': props.selectProps.menuIsOpen,
            })}
          ></i>
        )}
      </components.DropdownIndicator>
    ),
    ClearIndicator: (props) => (
      <components.ClearIndicator {...props}>
        <Icon icon="Close" size="medium" stroke="var(--quipli-black-800)" />
      </components.ClearIndicator>
    ),
    SingleValue: (props) => (
      <components.SingleValue {...props}>
        {props.hasValue ? (
          <Typography type="text-sm" className="text-quipli-black-800 text-ellipsis overflow-hidden">
            {props.children}
          </Typography>
        ) : (
          props.children
        )}
      </components.SingleValue>
    ),
    Control: (props) => (
      <div data-cy={dataCy}>
        <components.Control {...props} />
      </div>
    ),
    IndicatorSeparator: null,
  };
};

const handleCustomStyle = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  styles: StylesConfig<Option, IsMulti, Group>,
): StylesConfig<Option, IsMulti, Group> => {
  const mergedStyles = Object.entries(styles).map(([name, styleCallback]) => {
    const mergedStyle = (defaultStyle: any, state: any) => {
      const { override, ...provided } = styleCallback(defaultStyle, state);

      return {
        ...(!override && defaultStyle),
        ...provided,
      };
    };

    return [name, mergedStyle];
  });

  return Object.fromEntries(mergedStyles);
};

export const SelectForm = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  type = 'input',
  label,
  labelType,
  labelWeight,
  isRequired,
  description,
  dataCy,
  ...props
}: Props<Option, IsMulti, Group> & SelectFormProps) => {
  const customStyles: StylesConfig<Option, IsMulti, Group> = mergeStyles(
    defaultStyle(type),
    props.styles && handleCustomStyle(props.styles),
  );
  const customComponents: Partial<SelectComponents<Option, IsMulti, Group>> = {
    ...defaultComponents(type, dataCy),
    ...props.components,
  };

  return (
    <div className="w-full">
      <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />

      <Select
        {...props}
        styles={customStyles}
        components={customComponents}
        classNamePrefix={props.classNamePrefix || 'select-form-v2'}
        isSearchable={props.isSearchable !== undefined ? props.isSearchable : type !== 'inline'}
        backspaceRemovesValue={false}
        data-cy={dataCy}
        value={props.value}
      />

      <FieldDescription description={description} />
    </div>
  );
};

export const CreatableSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  creatableOptionText,
  onClickCreatableOption,
  type = 'input',
  dataCy,
  ...props
}: CreatableProps<Option, IsMulti, Group> & CreatableSelectFormProps) => {
  const customStyles: StylesConfig<Option, IsMulti, Group> = mergeStyles(
    defaultStyle(type),
    props.styles && handleCustomStyle(props.styles),
  );
  const customComponents: Partial<SelectComponents<Option, IsMulti, Group>> = {
    ...defaultComponents(type, dataCy, creatableOptionText),
    ...props.components,
  };

  return (
    <Creatable
      {...props}
      styles={customStyles}
      components={customComponents}
      classNamePrefix={props.classNamePrefix || 'select-form-v2'}
      createOptionPosition="first"
      isValidNewOption={(inputStr) => inputStr.length === 0}
      isSearchable={type === 'inline' ? false : props.isSearchable}
      onCreateOption={() => onClickCreatableOption && onClickCreatableOption()}
      backspaceRemovesValue={false}
      data-cy={dataCy}
    />
  );
};

export const AsyncSelectForm = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  type = 'input',
  dataCy,
  label,
  labelType,
  labelWeight,
  isRequired,
  description,
  ...props
}: AsyncCreatableProps<Option, IsMulti, Group> & SelectFormProps) => {
  const customStyles: StylesConfig<Option, IsMulti, Group> = mergeStyles(
    defaultStyle(type),
    props.styles && handleCustomStyle(props.styles),
  );
  const customComponents: Partial<SelectComponents<Option, IsMulti, Group>> = {
    ...defaultComponents(type, dataCy),
    ...props.components,
  };

  return (
    <div className="w-full">
      <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />
      <AsyncSelect
        {...props}
        styles={customStyles}
        components={customComponents}
        classNamePrefix={props.classNamePrefix || 'select-form-v2'}
        isSearchable={props.isSearchable !== undefined ? props.isSearchable : type !== 'inline'}
        backspaceRemovesValue={false}
        data-cy={dataCy}
        value={props.value}
        menuPortalTarget={document.body}
      />
      <FieldDescription description={description} />
    </div>
  );
};

export const AsyncCreatableSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  onClickCreatableOption,
  type = 'input',
  dataCy,
  ...props
}: AsyncCreatableProps<Option, IsMulti, Group> & CreatableSelectFormProps) => {
  const customStyles: StylesConfig<Option, IsMulti, Group> = mergeStyles(
    defaultStyle(type),
    props.styles && handleCustomStyle(props.styles),
  );
  const customComponents: Partial<SelectComponents<Option, IsMulti, Group>> = {
    ...defaultComponents(type, dataCy),
    ...props.components,
  };

  return (
    <AsyncCreatable
      {...props}
      styles={customStyles}
      components={customComponents}
      classNamePrefix={props.classNamePrefix || 'select-form-v2'}
      createOptionPosition="first"
      isValidNewOption={(inputStr) => inputStr.length === 0}
      onCreateOption={() => onClickCreatableOption && onClickCreatableOption()}
      backspaceRemovesValue={false}
      data-cy={dataCy}
    />
  );
};
